// NIGHT-DIRECTSTAFF-2026-10-19 §3: Matt's review of a finished exam -- the score per section, the questions they got
// wrong (each with their answer and the right one) first, then the ones they got right; and "Send result", which opens
// the Send box (Copy / Text / Email) with a short, kind message. Nothing is sent until Matt presses a button in it.
// A failed result carries a NEW link for a retake (links are stored scrambled; attempts carry over), made on the click.
import { useEffect, useState } from 'react';
import { examLink, PASS_PCT } from './lib/exams';
import { resultMessage, lockedMessage } from './lib/examReview';
import SendLink from './SendLink';

const btn = (bg, fg, off) => ({ background: bg, color: fg, borderRadius: 8, padding: '0 16px', minHeight: 44, fontSize: 13, fontWeight: 700, border: 0,
  cursor: off ? 'not-allowed' : 'pointer', opacity: off ? 0.5 : 1 });
const day = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');

/**
 * candidate, attemptId, examId; load(attemptId) -> the portal's review; api: makeExamsApi (for the retake link);
 * config.baseUrl: the portal; sendApi: the Send box's sends
 */
export default function ExamReview({ candidate, attemptId, examId, load, api, config = {}, sendApi = null, toast = () => {}, onClose, onSent = () => {}, locked = false }) {
  const [review, setReview] = useState(null);
  const [error, setError] = useState('');
  const [send, setSend] = useState(null);       // { link, linkRef, kind, body }
  const [busy, setBusy] = useState(false);
  useEffect(() => { let live = true; load(attemptId).then((r) => live && setReview(r)).catch((e) => live && setError(e.message)); return () => { live = false; }; }, [attemptId]);   // eslint-disable-line

  const startSend = async () => {
    if (review.passed) {
      setSend({ link: '', linkRef: attemptId, kind: 'exam_result', body: resultMessage({ name: candidate.name, review }) });
      return;
    }
    // §3 (10-21): after the second fail no retake link -- a link made now would itself unlock a third attempt.
    // Staff allow one with "Allow another attempt" in the Exams box (logged), never by sending a result.
    if (locked) {
      setSend({ link: '', linkRef: attemptId, kind: 'exam_result', body: lockedMessage({ name: candidate.name, review }) });
      return;
    }
    setBusy(true);
    try {
      const r = await api.send(candidate.id, [examId], null);
      const link = examLink(r.token, config.baseUrl);
      setSend({ link, linkRef: r.link_id, kind: 'exam', body: resultMessage({ name: candidate.name, review, link }) });
      onSent();
    } catch (e) { toast(e.message || 'Could not make the retake link'); } finally { setBusy(false); }
  };

  const wrong = review ? review.questions.filter((q) => !q.ok) : [];
  const right = review ? review.questions.filter((q) => q.ok) : [];
  return (
    <div style={{ position: 'fixed', inset: 0, background: '#0008', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1002 }} onClick={onClose}>
      <div className="card" role="dialog" aria-label="Exam review" data-testid="exam-review" onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(680px, calc(100vw - 16px))', maxHeight: '90vh', overflow: 'auto', padding: 18, boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
          <h3 style={{ fontSize: 16, fontWeight: 800, margin: 0 }}>{review ? review.title : 'Exam review'}</h3>
          <button className="nb" data-testid="exam-review-close" onClick={onClose} style={btn('#f1f5f9', '#475569')}>Close</button>
        </div>
        {!review && !error && <p style={{ fontSize: 13, color: '#475569' }}>Loading…</p>}
        {error && <p data-testid="exam-review-error" role="alert" style={{ fontSize: 13, color: '#b91c1c' }}>{error}</p>}
        {review && (
          <>
            <p data-testid="exam-review-score" style={{ fontSize: 14, margin: '6px 0 10px' }}>
              <strong style={{ color: review.passed ? '#15803d' : '#b91c1c' }}>{review.passed ? 'Passed' : 'Not passed'}</strong>
              {' '}· {review.pct}% ({review.correct} of {review.total}) · pass line {review.passPct || PASS_PCT}% · attempt {review.attempt_no} · {day(review.submitted_at)}
            </p>
            <table data-testid="exam-review-sections" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 12 }}>
              <caption style={{ textAlign: 'left', fontWeight: 700, fontSize: 12, color: '#475569', paddingBottom: 4 }}>By section</caption>
              <tbody>
                {review.sections.map((s) => (
                  <tr key={s.name} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <th scope="row" style={{ textAlign: 'left', fontWeight: 500, padding: '4px 6px 4px 0' }}>{s.name}</th>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap', padding: 4, color: s.pct < (review.passPct || PASS_PCT) ? '#b91c1c' : '#166534', fontWeight: 600 }}>
                      {s.correct}/{s.total} · {s.pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <h4 style={{ fontSize: 13, margin: '0 0 6px' }}>Wrong ({wrong.length})</h4>
            <ol data-testid="exam-review-wrong" style={{ listStyle: 'none', padding: 0, margin: '0 0 12px' }}>
              {wrong.map((q) => (
                <li key={q.n} data-testid="exam-review-q" style={{ borderTop: '1px solid #f1f5f9', padding: '8px 0', fontSize: 13 }}>
                  <div style={{ color: '#475569', fontSize: 12 }}>Question {q.n} · {q.section}</div>
                  <div style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{q.stem}</div>
                  <div style={{ color: '#b91c1c' }}>✕ Their answer: {q.given === null ? 'no answer' : q.given}</div>
                  <div style={{ color: '#166534' }}>✓ Right answer: {q.right}</div>
                </li>
              ))}
            </ol>
            <details data-testid="exam-review-right">
              <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 700, minHeight: 44, display: 'flex', alignItems: 'center' }}>▸ Right ({right.length}) · tap to show</summary>
              <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {right.map((q) => (
                  <li key={q.n} style={{ borderTop: '1px solid #f1f5f9', padding: '6px 0', fontSize: 13 }}>
                    <span style={{ color: '#475569', fontSize: 12 }}>Q{q.n} · {q.section}</span> — {q.stem} <span style={{ color: '#166534' }}>✓ {q.right}</span>
                  </li>
                ))}
              </ol>
            </details>
            {!send && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                <button className="nb" data-testid="exam-review-send" disabled={busy || !sendApi} onClick={startSend} style={btn('#0e7490', 'white', busy || !sendApi)}>
                  {busy ? 'Making the retake link…' : review.passed || locked ? 'Send result' : 'Send result + retake link'}</button>
              </div>
            )}
            {!send && !review.passed && !locked && <p style={{ fontSize: 12, color: '#475569', margin: '6px 0 0' }}>Makes a new link to this exam for the retake (the first link can't be shown again). Nothing is sent until you press Text or Email.</p>}
            {!send && !review.passed && locked && <p data-testid="exam-review-locked" style={{ fontSize: 12, color: '#92400e', margin: '6px 0 0' }}>Both attempts are used, so the result goes without a link. To give one more try, use "Allow another attempt" in the Exams box.</p>}
            {send && sendApi && (
              <SendLink kind={send.kind} link={send.link} linkRef={send.linkRef} candidate={candidate} api={sendApi} toast={toast} onSent={onSent} initialBody={send.body} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
